import React, { useEffect, useState } from 'react';
import { Box, Button, Paper, Typography, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete } from '@mui/icons-material';
import api from '../../utils/api';
import { Broker } from '../../types';
import PageHeader from '../../components/Common/PageHeader';
import AddBrokerDialog from '../../components/Brokers/AddBrokerDialog';
import EditBrokerDialog from '../../components/Brokers/EditBrokerDialog';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import ToastNotification from '../../utils/toastNotification';

const BrokersPage: React.FC = () => {
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<Broker | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [brokerToDelete, setBrokerToDelete] = useState<Broker | null>(null);

  useEffect(() => {
    loadBrokers();
  }, []);

  const loadBrokers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/brokers');
      setBrokers(response.data);
    } catch (error) {
      console.error('Error loading brokers:', error);
      ToastNotification.error('Failed to load brokers');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (broker: Broker) => {
    setSelectedBroker(broker);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (broker: Broker) => {
    setBrokerToDelete(broker);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!brokerToDelete) return;
    try {
      await api.delete(`/brokers/${brokerToDelete.id}`);
      ToastNotification.success('Broker deleted successfully');
      await loadBrokers();
    } catch (error) {
      console.error('Error deleting broker:', error);
      ToastNotification.error('Failed to delete broker');
    } finally {
      setDeleteDialogOpen(false);
      setBrokerToDelete(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.row.name}
        </Typography>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 2,
      minWidth: 200,
      renderCell: (params) =>
        params.row.notes || (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Brokers"
        subtitle="Competitor brokers"
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={() => setAddDialogOpen(true)}>
            Add Broker
          </Button>
        }
      />

      <Paper sx={{ height: 600, width: '100%', mt: 2 }}>
        <DataGrid
          rows={brokers}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50]}
        />
      </Paper>

      <AddBrokerDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={loadBrokers}
      />

      <EditBrokerDialog
        open={editDialogOpen}
        broker={selectedBroker}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedBroker(null);
        }}
        onSuccess={loadBrokers}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Broker"
        message={`Are you sure you want to delete ${brokerToDelete?.name}?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default BrokersPage;